import { NgClass } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationItem, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { Helpers } from 'app/core/helpers/helpers';
import { SideMenuService } from 'app/core/services/side-menu.service';
import { AuthService, PROFILE_COMPLETION_PROMPTED_KEY } from 'app/modules/auth/auth.service';
import { DashboardService } from 'app/modules/dashboard/dashboard.service';
import moment from 'moment';
import { filter, Subject, takeUntil } from 'rxjs';
import { AccessBlockComponent } from './components/access-block/access-block.component';
import { NotificationsComponent } from './components/notifications/notifications.component';
import { UserComponent } from './components/user/user.component';
import { AccountStatusDialogComponent } from './dialogs/account-status-dialog/account-status-dialog.component';
import { ProfileCompletionDialogComponent } from './dialogs/profile-completion-dialog/profile-completion-dialog.component';

@Component({
  selector: 'app-layout-app',
  templateUrl: './layout-app.component.html',
  styleUrls: ['./layout-app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatMenuModule,
    MatSelectModule,
    MatTooltipModule,
    RouterOutlet,
    UserComponent,
    NotificationsComponent,
    NgClass,
  ],
})
export class LayoutAppComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  navigationAppearance: 'default' | 'dense' = 'dense';
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _storageKey: string = 'navigation_appearance';
  private readonly _profileCompletionKey: string = PROFILE_COMPLETION_PROMPTED_KEY;

  menuItems: FuseNavigationItem[] = [];

  protected dashboardSvc = inject(DashboardService);
  protected _authService = inject(AuthService);
  private router = inject(Router);
  private _matDialog = inject(MatDialog);

  // access block state
  enableAccess: boolean = false;
  disableComment: any = '';
  countDown: any;
  private accountStatusOpen: boolean = false;

  // Dashboard date range options (shown in header on dashboard route)
  dateRangeOptions: { value: string; label: string }[] = [
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'all', label: 'All Time' },
  ];

  constructor(
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private _sideMenuService: SideMenuService
  ) {
    this.menuItems = this._sideMenuService.menuItems;
  }

  get currentYear(): number {
    return new Date().getFullYear();
  }

  ngOnInit(): void {
    this.navigationAppearance = (localStorage.getItem(this._storageKey) as 'default' | 'dense') || this.navigationAppearance;

    this._fuseMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
      this.isScreenSmall = !matchingAliases.includes('md');
      this.navigationAppearance = this.isScreenSmall ? 'default' : this.navigationAppearance;
    });

    // Initialize the dashboard date range so widgets get values on first load
    this.applyDashboardDateRange(this.dashboardSvc.dateRange());

    this.companyenableAccess();
    this.checkAccountStatus();
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((event) => {
      this.companyenableAccess();
    });

    if (this.enableAccess) {
      this.showProfileCompletion();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  toggleNavigation(name: string): void {
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);
    if (navigation) {
      navigation.toggle();
    }
  }

  toggleNavigationAppearance(): void {
    this.navigationAppearance = this.navigationAppearance === 'dense' ? 'default' : 'dense';
    localStorage.setItem(this._storageKey, this.navigationAppearance);
  }

  selectDashboardView(view: string): void {
    this.dashboardSvc.dashboardView.set(view);
    this.dashboardSvc.updateProfileView(view).subscribe();
    const profile = this._authService.profile();
    if (profile) {
      this._authService.profile.set({ ...profile, view });
    }
  }

  onDashboardDateRangeChange(value: string): void {
    this.dashboardSvc.dateRange.set(value);
    this.applyDashboardDateRange(value);
  }

  private applyDashboardDateRange(value: string): void {
    const dateRange = Helpers.getDateRange(value);
    if (!Helpers.empty(dateRange.from) && !Helpers.empty(dateRange.to)) {
      this.dashboardSvc.fromDate.set(dateRange.from);
      this.dashboardSvc.toDate.set(dateRange.to);
    }
  }

  get isDashboardRoute(): boolean {
    return this.router.url === '/dashboard';
  }

  private showProfileCompletion(): void {
    const profile = this._authService.profile();
    if (!profile?.id) return;
    const isIncomplete = !profile.designation || !profile.profile_image_url;
    if (!isIncomplete) return;

    if (profile.profile_reminder_date && moment(profile.profile_reminder_date).isAfter()) {
      return;
    }

    const key = `${this._profileCompletionKey}-${profile.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');

    this._matDialog.open(ProfileCompletionDialogComponent, {});
  }

  public checkAccountStatus() {
    const profile = this._authService.profile();
    if (!profile) return;

    if (profile.company.status === 0 && !this.accountStatusOpen) {
      this.accountStatusOpen = true;
      this._matDialog.open(AccountStatusDialogComponent, {
        disableClose: true,
        autoFocus: false,
      });
    }
  }

  public companyenableAccess() {
    const profile = this._authService.profile();
    if (!profile) return;

    this.enableAccess = profile.company.enable_modules;
    this.disableComment = profile.company.disable_comment;
    this.countDown = profile.company.disable_time;

    if (!this.enableAccess) {
      this._matDialog.open(AccessBlockComponent, {
        data: { disableComment: this.disableComment, countDown: this.countDown },
        disableClose: true,
        width: '400px',
      });
    }
  }
}
